import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { Form, Select } from 'antd';
import UserGroupSelect from './UserGroupSelect';
import useMountEffect from '../../hooks/useMountEffect';
import {
	fetchUserGroupsForMember,
	fetchUserGroupsForPicker,
} from '../../thunks/user_groups_thunks';
import { BUYER_ADMIN_ONLY, hasRequiredRole, SUPPLIER_ADMIN_ONLY } from '../roles/roles';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { USER_GROUPS_FOR_MEMBER_TARGET_COLLECTION } from '../../types/user_groups';
import { withUserType } from '../withUserType';
import { isAdminUserGroupsEnabled } from '../../utils/FeatureFlagUtils';

const FormItem = Form.Item;

export const USER_GROUP_IDS_FIELD = 'userGroupIds';

const USER_GROUPS_LABEL = 'User Groups';

// The "User Groups" field shared by the buyer and supplier invite / edit user modals. It sits
// next to Roles and behaves the same way, except that a user may be in several groups. Drop it
// anywhere inside one of those forms and it takes care of the rest.
//
// It owns the two reads as well as the field: the tenant's groups, which become the options, and
// (when an existing user is opened) that user's current groups, which become the value. Both are
// fired on mount and land in Redux. The thunks coalesce in-flight requests, which matters here
// because the forms swap their whole body for a <Spin/> while the user loads — so this mounts
// once as the modal opens, is torn down, and mounts again.
//
// The user's current groups reach the field as the Form.Item's `initialValue`, so the field is not
// mounted until that read settles — a loading placeholder stands in until then. That also means
// the admin cannot pick groups that a late-landing read would then overwrite. The edit forms use
// `preserve={false}`, so every remount drops the field's value and re-applies `initialValue`.
//
// Nothing renders and nothing is fetched for a non-admin. Every `user_groups` route is
// admin-gated on the backend, and the gate answers 400 ("Not an admin user") rather than
// 401/403, so a non-admin would otherwise get a picker that fails to load. That reaches past the
// two admin users pages: FieldTechsIndexPage and the field-tech analytics page both open the
// supplier invite modal, and either is reachable by any supplier role.
//
// Likewise nothing renders and nothing is fetched while the Admin-User-Groups feature flag is off.
//
// Leaving the field out for those users also gives the save its guard — the value is `undefined`,
// so `syncUserGroupsForUser` makes no request and their membership is left alone. The same holds
// if the membership read fails: the field mounts unseeded, and stays out of the save unless the
// admin actually picks something.
const UserGroupsFormItem: FC<any> = ({
	currentUser,
	userType,
	memberEmail,
	userGroups,
	fetchGroups,
	fetchGroupsForMember,
}): React.ReactElement => {
	const canManageUserGroups =
		isAdminUserGroupsEnabled() &&
		hasRequiredRole(
			nullSafeGetOrElse('roles', currentUser, []),
			userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ADMIN_ONLY : BUYER_ADMIN_ONLY
		);

	// Waits on this mount's own read, not on whatever the global collection already holds — a
	// previous open of the same user would otherwise seed the field from a stale membership.
	const [memberReadSettled, setMemberReadSettled] = useState(!memberEmail);

	useMountEffect(() => {
		if (!canManageUserGroups) {
			return;
		}
		fetchGroups().catch(() => {});
		if (memberEmail) {
			const settle = () => setMemberReadSettled(true);
			fetchGroupsForMember(memberEmail).then(settle, settle);
		}
	});

	if (!canManageUserGroups) {
		return null;
	}

	if (!memberReadSettled) {
		return (
			<FormItem label={USER_GROUPS_LABEL}>
				<Select
					className="w-full"
					mode="multiple"
					disabled
					loading
					placeholder="Loading user groups"
				/>
			</FormItem>
		);
	}

	// The collection is global, so only seed from it when it holds this user's membership — a
	// failed read leaves the previous user's there. `filters` is recorded by the reducer because
	// the thunk passes `memberEmail` as a filter rather than a bare query param.
	const loadedMemberEmail = nullSafeGet(
		`${USER_GROUPS_FOR_MEMBER_TARGET_COLLECTION}.filters.memberEmail`,
		userGroups
	);
	const memberGroupsAreLoaded =
		!!memberEmail && compareStrings(loadedMemberEmail, memberEmail.toLowerCase().trim());

	// The reducer stringifies record ids, but the field's value must stay numeric — the backend
	// reads `userGroupIds` as a list of numbers and silently ignores entries that are not, which
	// would read as "this user is in no groups". Resolve each id back to its record and take the
	// real `id` off it.
	const records = nullSafeGetOrElse('records', userGroups, {});
	const memberGroupIds = memberGroupsAreLoaded
		? nullSafeGetOrElse(`${USER_GROUPS_FOR_MEMBER_TARGET_COLLECTION}.recordsIds`, userGroups, [])
				.map((recordId) => nullSafeGet('id', records[recordId]))
				.filter((groupId) => groupId !== undefined && groupId !== null)
		: undefined;

	return (
		<FormItem name={USER_GROUP_IDS_FIELD} label={USER_GROUPS_LABEL} initialValue={memberGroupIds}>
			<UserGroupSelect />
		</FormItem>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	userGroups: state.user_groups,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchGroups: () => dispatch(fetchUserGroupsForPicker(ownProps.userType)()),
	fetchGroupsForMember: (email) => dispatch(fetchUserGroupsForMember(ownProps.userType)(email)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(UserGroupsFormItem));
